import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import supportApiService from '../../services/supportApiService';

const SupportPaymentInfo = () => {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPayments, setFilteredPayments] = useState([]);

  useEffect(() => {
    loadPaymentInfo();
  }, []);

  useEffect(() => {
    // Filter payments based on search term
    if (searchTerm.trim() === '') {
      setFilteredPayments(payments);
    } else {
      const filtered = payments.filter(payment => 
        payment.agentId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.amount?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.modeOfPayment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.balanceAmount?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.transactionId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPayments(filtered);
    }
  }, [searchTerm, payments]);

  const loadPaymentInfo = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading payment info...');
      const data = await supportApiService.getPaymentInfo();
      console.log('📊 Payment Info API Response:', data);
      
      if (data.success) {
        setPayments(data.payment || []);
        setFilteredPayments(data.payment || []);
        console.log('✅ Payment info loaded successfully:', data.payment?.length, 'payments');
      } else {
        console.error('❌ Payment Info API returned error:', data.message);
        toast.error(data.message || 'Failed to load payment info');
      }
    } catch (error) {
      console.error('❌ Payment info load error:', error);
      toast.error('Failed to load payment info');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const getPaymentModeBadge = (mode) => {
    if (!mode) return <span className="text-gray-500">-</span>;
    
    const modeLower = mode.toLowerCase();
    let badgeClass = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    
    switch (modeLower) {
      case 'online':
      case 'card':
      case 'upi':
        badgeClass += ' bg-green-100 text-green-800';
        break;
      case 'cash':
        badgeClass += ' bg-blue-100 text-blue-800';
        break;
      case 'bank transfer':
      case 'neft':
      case 'rtgs':
        badgeClass += ' bg-purple-100 text-purple-800';
        break;
      default:
        badgeClass += ' bg-gray-100 text-gray-800';
    }
    
    return <span className={badgeClass}>{mode}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Payment Details</h1>
          <p className="text-gray-600">View and manage payment information</p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Payment Details</h3>
              <div className="text-sm text-gray-500">
                Total: {filteredPayments.length} payments
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  placeholder="Search by agent ID, amount, transaction ID..."
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Mode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction ID
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPayments.length > 0 ? (
                  filteredPayments.map((payment, index) => (
                    <tr 
                      key={payment.transactionId || index} 
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {payment.agentId || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getPaymentModeBadge(payment.modeOfPayment)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatCurrency(payment.balanceAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(payment.transactionDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                        {payment.transactionId || '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? 'No payments found matching your search.' : 'No payment details found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Summary Footer */}
          {filteredPayments.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-gray-700">
                  Showing {filteredPayments.length} of {payments.length} payments
                  {searchTerm && (
                    <span className="ml-2">
                      (filtered by "{searchTerm}")
                    </span>
                  )}
                </div>
                
                {/* Payment Summary */}
                <div className="flex gap-6 text-sm">
                  <div className="text-gray-600">
                    <span className="font-medium">Total Amount: </span>
                    <span className="font-bold text-green-600">
                      {formatCurrency(
                        filteredPayments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0)
                      )}
                    </span>
                  </div>
                  <div className="text-gray-600">
                    <span className="font-medium">Total Balance: </span>
                    <span className="font-bold text-blue-600">
                      {formatCurrency(
                        filteredPayments.reduce((sum, payment) => sum + (parseFloat(payment.balanceAmount) || 0), 0)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportPaymentInfo;